import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { MEDIA, CHURCH, REAL_PHOTOS } from "@/lib/church";
import { CalendarBlank, MapPin, Clock, ArrowRight, Sparkle, BookOpen, MusicNotes, ForkKnife, Cross, Drop, Crown, Sun } from "@phosphor-icons/react";

const RECURRING_KEYS = ["bible study", "prayer meeting", "sunday worship", "choir rehearsal"];
const MONTHLY_KEYS = ["holy communion", "baptisms", "mass food distribution"];

function classify(title) {
    const t = (title || "").toLowerCase();
    if (RECURRING_KEYS.some(k => t.includes(k))) return "weekly";
    if (MONTHLY_KEYS.some(k => t.includes(k))) return "monthly";
    return "special";
}

function iconFor(title) {
    const t = (title || "").toLowerCase();
    if (t.includes("bible") || t.includes("prayer")) return BookOpen;
    if (t.includes("choir")) return MusicNotes;
    if (t.includes("food")) return ForkKnife;
    if (t.includes("communion")) return Cross;
    if (t.includes("baptism")) return Drop;
    if (t.includes("lay annual") || t.includes("annual day")) return Crown;
    if (t.includes("worship") || t.includes("sunday")) return Sun;
    return Sparkle;
}

function formatDateParts(iso) {
    const d = new Date(iso + "T12:00:00");
    return {
        month: d.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
        day: d.getDate(),
        weekday: d.toLocaleDateString("en-US", { weekday: "long" }),
        year: d.getFullYear(),
        full: d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }),
    };
}

export default function Events() {
    const [events, setEvents] = useState([]);
    useEffect(() => {
        api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {});
    }, []);

    const sorted = [...events].sort((a, b) => a.date.localeCompare(b.date));
    const weekly = sorted.filter(e => classify(e.title) === "weekly");
    const monthly = sorted.filter(e => classify(e.title) === "monthly");
    const special = sorted.filter(e => classify(e.title) === "special");

    return (
        <div data-testid="events-page" className="bg-white">
            {/* HERO */}
            <section className="relative min-h-[70vh] flex items-end overflow-hidden bg-[#2E1A47]">
                <div className="absolute inset-0">
                    <img src={REAL_PHOTOS.PASTOR_PULPIT} alt="" className="w-full h-full object-cover opacity-80" style={{ objectPosition: "center 42%" }} />
                    <div className="absolute inset-0 bg-gradient-to-br from-[#2E1A47]/70 via-[#4C1D95]/55 to-[#2E1A47]/70" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2E1A47]/90 via-transparent to-transparent" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 w-full">
                    <div className="max-w-4xl">
                        <p className="overline text-[#FFC72C] mb-4 animate-fade-up">Our Rhythm of Faith</p>
                        <h1 className="font-display font-black tracking-tighter text-white text-6xl sm:text-7xl lg:text-[8rem] xl:text-[9rem] leading-[0.85] animate-fade-up animate-delay-100" data-testid="events-title">
                            Where the<br />
                            <span className="text-[#FFC72C] italic">church</span> shows up.
                        </h1>
                        <p className="mt-8 text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed animate-fade-up animate-delay-200">
                            Every week. Every month. In worship, in service, in joy. Here&rsquo;s what&rsquo;s ahead at Greater Mount Olive — come be part of it.
                        </p>
                    </div>
                </div>
            </section>

            {/* WEEKLY RHYTHM */}
            {weekly.length > 0 && (
                <section className="bg-[#F4F7F9] py-20 lg:py-28" data-testid="events-weekly-section">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12 items-end">
                            <div className="lg:col-span-8">
                                <p className="overline text-[#4C1D95] mb-4">Weekly Rhythm</p>
                                <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter text-[#2E1A47] leading-[0.9]">
                                    The heartbeat<br />
                                    <span className="text-[#4C1D95] italic">of our week</span>.
                                </h2>
                            </div>
                            <div className="lg:col-span-4 lg:text-right">
                                <p className="text-slate-600 leading-relaxed">A standing invitation. Same time. Same place. Every week. All are welcome.</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {weekly.map((ev) => {
                                const dp = formatDateParts(ev.date);
                                const Icon = iconFor(ev.title);
                                return (
                                    <div key={ev.id} data-testid={`event-weekly-${ev.id}`} className="bg-white p-7 lg:p-8 hover:bg-[#4C1D95] hover:text-white transition-all duration-300 group cursor-default">
                                        <Icon size={36} weight="duotone" className="text-[#FFC72C] mb-5" />
                                        <p className="overline text-[#4C1D95] group-hover:text-[#FFC72C] mb-3 transition-colors">{dp.weekday}s</p>
                                        <h3 className="font-display text-xl lg:text-2xl font-black tracking-tight leading-tight mb-3">{ev.title}</h3>
                                        <p className="text-sm font-bold text-[#2E1A47] group-hover:text-[#FFC72C] mb-2 transition-colors">{ev.time}</p>
                                        <p className="text-xs text-slate-500 group-hover:text-white/70 transition-colors">{ev.location}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* MONTHLY SACRED MOMENTS */}
            {monthly.length > 0 && (
                <section className="bg-white py-20 lg:py-28 relative overflow-hidden" data-testid="events-monthly-section">
                    <div className="absolute -right-32 top-20 w-96 h-96 bg-[#FFC72C]/10 blur-3xl rounded-full" />
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <p className="overline text-[#4C1D95] mb-4">Sacred Moments · Monthly Tradition</p>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter text-[#2E1A47] leading-[0.9] max-w-4xl mx-auto">
                                The <span className="text-[#FFC72C] italic">holy</span> rhythm of the month.
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                            {monthly.map((ev, i) => {
                                const Icon = iconFor(ev.title);
                                const dark = i === 1;
                                return (
                                    <div key={ev.id} data-testid={`event-monthly-${ev.id}`} className={`p-10 lg:p-12 relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300 ${dark ? 'bg-[#2E1A47] text-white' : 'bg-[#F4F7F9] text-[#2E1A47]'}`}>
                                        <Icon size={64} weight="duotone" className={`mb-8 ${dark ? 'text-[#FFC72C]' : 'text-[#4C1D95]'}`} />
                                        <p className={`overline mb-4 ${dark ? 'text-[#FFC72C]' : 'text-[#4C1D95]'}`}>Monthly Tradition</p>
                                        <h3 className="font-display text-3xl lg:text-4xl font-black tracking-tight leading-[0.95] mb-5">{ev.title}</h3>
                                        <p className={`text-sm leading-relaxed mb-6 ${dark ? 'text-white/80' : 'text-slate-600'}`}>{ev.description}</p>
                                        <div className={`flex flex-wrap gap-4 text-xs font-bold uppercase tracking-widest pt-5 border-t ${dark ? 'border-white/15 text-white/70' : 'border-slate-200 text-slate-500'}`}>
                                            <span className="flex items-center gap-2"><Clock size={12} weight="bold" /> {ev.time}</span>
                                            <span className="flex items-center gap-2"><MapPin size={12} weight="bold" /> {ev.location}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* SPECIAL EVENTS */}
            {special.length > 0 && (
                <section className="bg-[#2E1A47] text-white py-20 lg:py-28 relative overflow-hidden" data-testid="events-special-section">
                    <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] bg-[#4C1D95]/40 blur-3xl rounded-full" />
                    <div className="absolute -bottom-32 -right-32 w-[40rem] h-[40rem] bg-[#FFC72C]/10 blur-3xl rounded-full" />
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="mb-16">
                            <p className="overline text-[#FFC72C] mb-4">Mark Your Calendar</p>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.9]">
                                Special <span className="text-[#FFC72C] italic">celebrations</span>.
                            </h2>
                        </div>
                        <div className="space-y-4">
                            {special.map((ev) => {
                                const dp = formatDateParts(ev.date);
                                const Icon = iconFor(ev.title);
                                if (ev.flyer) {
                                    return (
                                        <div key={ev.id} data-testid={`event-special-${ev.id}`} className="relative overflow-hidden bg-gradient-to-br from-[#4C1D95]/40 via-[#2E1A47] to-[#2E1A47] border border-[#FFC72C]/30 shadow-[0_0_60px_-15px_rgba(255,199,44,0.35)]">
                                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                                                <div className="lg:col-span-6 relative bg-[#1a0f2e] flex items-center justify-center p-6 lg:p-10">
                                                    <img src={ev.flyer} alt={ev.title} className="max-w-full max-h-[720px] w-auto h-auto shadow-2xl" data-testid={`event-flyer-${ev.id}`} />
                                                </div>
                                                <div className="lg:col-span-6 p-8 lg:p-12 flex flex-col justify-center">
                                                    <div className="inline-flex items-center gap-2 mb-6">
                                                        <span className="bg-[#FFC72C] text-[#2E1A47] px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-black">Featured Event</span>
                                                        <Icon size={22} weight="duotone" className="text-[#FFC72C]" />
                                                    </div>
                                                    <div className="flex items-baseline gap-4 mb-6">
                                                        <div className="bg-[#FFC72C] text-[#2E1A47] px-5 py-3">
                                                            <p className="font-display text-[10px] uppercase tracking-[0.2em] font-black leading-none">{dp.month}</p>
                                                            <p className="font-display text-5xl lg:text-6xl font-black leading-none tracking-tighter mt-1">{dp.day}</p>
                                                        </div>
                                                        <p className="text-xs uppercase tracking-widest font-bold text-white/60">{dp.weekday}</p>
                                                    </div>
                                                    <h3 className="font-display text-3xl lg:text-4xl xl:text-5xl font-black tracking-tighter leading-[0.95] mb-5 text-white">{ev.title}</h3>
                                                    <p className="text-sm lg:text-base text-white/80 leading-relaxed mb-8">{ev.description}</p>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-white/15">
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-widest text-[#FFC72C] font-bold mb-2 flex items-center gap-2"><Clock size={12} weight="bold" /> Time</p>
                                                            <p className="text-sm text-white font-medium">{ev.time}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-widest text-[#FFC72C] font-bold mb-2 flex items-center gap-2"><MapPin size={12} weight="bold" /> Location</p>
                                                            <p className="text-sm text-white font-medium">{ev.location}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <div key={ev.id} data-testid={`event-special-${ev.id}`} className="bg-white/[0.03] hover:bg-white/[0.07] backdrop-blur-sm border border-white/10 p-8 lg:p-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center transition-colors group">
                                        <div className="md:col-span-2 flex items-center md:flex-col md:items-start gap-4 md:gap-2">
                                            <div className="bg-[#FFC72C] text-[#2E1A47] px-4 py-2 inline-block">
                                                <p className="font-display text-[10px] uppercase tracking-[0.2em] font-black leading-none">{dp.month}</p>
                                                <p className="font-display text-4xl lg:text-5xl font-black leading-none tracking-tighter mt-1">{dp.day}</p>
                                            </div>
                                            <p className="text-[10px] uppercase tracking-widest font-bold text-white/50">{dp.weekday}</p>
                                        </div>
                                        <div className="md:col-span-7">
                                            <div className="flex items-center gap-3 mb-3">
                                                <Icon size={20} weight="duotone" className="text-[#FFC72C]" />
                                                <h3 className="font-display text-2xl lg:text-3xl font-black tracking-tight leading-tight">{ev.title}</h3>
                                            </div>
                                            <p className="text-sm text-white/75 leading-relaxed">{ev.description}</p>
                                        </div>
                                        <div className="md:col-span-3 space-y-2 text-sm text-white/75 md:text-right">
                                            <p className="flex items-center gap-2 md:justify-end font-bold text-white"><Clock size={14} weight="bold" className="text-[#FFC72C]" /> {ev.time}</p>
                                            <p className="flex items-center gap-2 md:justify-end"><MapPin size={14} weight="bold" className="text-[#FFC72C]" /> {ev.location}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* EMPTY STATE */}
            {events.length === 0 && (
                <section className="bg-white py-32">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <CalendarBlank size={64} weight="duotone" className="text-[#4C1D95] mx-auto mb-6" />
                        <p className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-[#2E1A47]">No events scheduled.</p>
                        <p className="mt-4 text-slate-600">Check back soon — we&rsquo;re always planning something good.</p>
                    </div>
                </section>
            )}

            {/* CTA */}
            <section className="bg-[#FFC72C] text-[#2E1A47] py-20 lg:py-24 relative overflow-hidden" data-testid="events-cta">
                <div className="absolute inset-y-0 right-0 w-1/3 bg-[#2E1A47]/5" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <p className="overline text-[#4C1D95] mb-4">Stay Connected</p>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9] mb-6">
                            Never miss a Sunday.
                        </h2>
                        <p className="text-base lg:text-lg text-[#2E1A47]/80 leading-relaxed max-w-xl">
                            Subscribe to the newsletter and we&rsquo;ll send you weekly event reminders, sermon updates, and notes from Pastor Woods.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
                        <Link to="/contact" data-testid="events-subscribe-btn" className="inline-flex items-center gap-3 bg-[#2E1A47] hover:bg-[#4C1D95] text-white px-10 py-5 text-sm font-black uppercase tracking-widest transition-colors">
                            Subscribe <ArrowRight size={16} weight="bold" />
                        </Link>
                        <a href={CHURCH.phoneHref} data-testid="events-call-btn" className="inline-flex items-center gap-3 border-2 border-[#2E1A47] hover:bg-[#2E1A47] hover:text-[#FFC72C] text-[#2E1A47] px-10 py-5 text-sm font-black uppercase tracking-widest transition-colors">
                            Call · {CHURCH.phone}
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
